"use client";

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Users, ArrowRight, Plus, ClipboardPaste, Eye, MessageSquare, Hand } from "lucide-react"
import { motion } from "framer-motion"

export default function PlanningPokerPage() {
  const router = useRouter();
  const [sessionId, setSessionId] = useState('');
  const [joinError, setJoinError] = useState('');

  const handleJoinSession = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId.trim()) {
      setJoinError('Please enter a session ID');
      return;
    }
    router.push(`/agile/planning-poker/join?id=${sessionId}`);
  };

  const handleCreateSession = () => {
    router.push('/agile/planning-poker/create');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setSessionId(text.trim());
      setJoinError('');
    } catch { }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-16"
      >
        <h1 className="text-5xl font-extrabold tracking-tight mb-4">
          <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Planning Poker
          </span>
        </h1>
        <p className="text-lg text-muted-foreground">
          Estimate as a team, in real time.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <Card className="h-full rounded-2xl border shadow-sm hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-lg">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-950">
                  <Plus className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                Create Session
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Start a new session and invite your team to estimate together.
              </p>
              <Button
                onClick={handleCreateSession}
                className="w-full"
              >
                Create New Session
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <Card className="h-full rounded-2xl border shadow-sm hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-lg">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-950">
                  <Users className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                Join Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleJoinSession} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="session-id">Session ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="session-id"
                      placeholder="Enter session ID"
                      value={sessionId}
                      onChange={(e) => {
                        setSessionId(e.target.value);
                        setJoinError('');
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handlePaste}
                      className="shrink-0"
                      aria-label="Paste from clipboard"
                    >
                      <ClipboardPaste className="h-4 w-4" />
                    </Button>
                  </div>
                  {joinError && <p className="text-sm text-destructive">{joinError}</p>}
                </div>
                <Button type="submit" className="w-full">
                  Join Session
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        className="rounded-2xl bg-muted/50 px-8 py-10"
      >
        <h2 className="text-center text-sm font-medium uppercase tracking-widest text-muted-foreground mb-10">
          How it works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-950">
              <Hand className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-medium">Vote</p>
            <p className="text-sm text-muted-foreground">Everyone picks a card privately</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-950">
              <Eye className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-sm font-medium">Reveal</p>
            <p className="text-sm text-muted-foreground">All cards are revealed at once</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
              <MessageSquare className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-medium">Discuss</p>
            <p className="text-sm text-muted-foreground">Align on the final estimate</p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
